import { TABLE_TOP_CORNERS } from "../../../v2/components/tableHeaderCorners.js";
// src/features/projects/hub/PeopleRolesMatrix.jsx
//
// Participants matrix (Projects hub).
// Project rows x (three ownership slots + four canonical tracks). Cells show
// persisted participants and a primary/DRI avatar dot; empty cells
// are explicit coverage gaps. Write controls render only for rows the caller
// may manage (explicit admins only). The server stays the gate:
// every 403 triggers a revalidation of identity + rows.

import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ToolbarViewToggle } from "../../../v2/components/ToolbarViewToggle.jsx";
import { UNSAFE_LocationContext } from "react-router-dom";
import { T } from "../../../app/tokens.js";
import { ProjectsTableScroll } from "../ProjectsScrollLayout.jsx";
import { Modal } from "../../../components/Modal.jsx";
import { PhotoOrInitials } from "../../../components/PhotoOrInitials.jsx";
import { Lock } from "lucide-react";
import { ParticipantsKey } from "./ParticipantsKey.jsx";
import { useParticipantDisplayPreference } from "./useParticipantDisplayPreference.js";
import {
  getProjectParticipants, putProjectParticipant, deleteProjectParticipant, getPeopleDirectory,
  getMyAssignments, getPortfolioRoles, putPortfolioRoles,
} from "../../../data/projectWeeklyClient.js";
import {
  MATRIX_COLUMNS, PORTFOLIO_ROLE_LABELS, initials, cellPeople, cellHasPrimary, coverage,
   canManageRow, pickable, personKey, toggledRoles, rolesByPerson, coverageRows, MISSING_OPTIONS, isParticipantArray,
} from "./peopleRolesModel.js";
import { btn, inputStyle, muted, withDisabled } from "../weekly/weeklyUi.js";

const TH = { padding: "9px 8px", fontSize: T.fsXxs, fontWeight: T.fwBold, color: T.mid, textTransform: "uppercase", letterSpacing: ".06em", borderBottom: `1px solid ${T.border}`, background: "var(--v2-tint)", whiteSpace: "nowrap" };

function OrgChip({ org }) {
  if (!org) return null;
  return <span data-testid="org-chip" style={{ fontSize: T.fsXxxs || T.fsXxs, fontWeight: T.fwBold, borderRadius: 4, padding: "0 4px", border: `1px solid ${T.border}`, color: org === "JLL" ? T.ragAmberText : T.blue, background: T.card }}>{org}</span>;
}

function Avatar({ person, names = false, size = 26 }) {
  const demo = person.isDemo;
  const color = person.org === "JLL" ? T.ragAmberText : person.org === "Ripple" ? T.blue : T.mid;
   const identity = `${person.displayName || "Unknown"}${person.org ? ` · ${person.org}` : " · affiliation not recorded"}${person.isPrimary ? " · primary (DRI)" : ""}${demo ? " · demo person" : ""}${person.active === false ? " · inactive" : ""}`;
  return (
    <span title={identity} aria-label={identity} tabIndex={0}
      style={{ display: "inline-flex", alignItems: "center", gap: 5, minWidth: 0 }}>
      <span style={{ position: "relative", display: "inline-flex", flexShrink: 0 }}>
      <PhotoOrInitials identity={personKey(person)} photoUrl={person.photoUrl} size={size}
        initials={initials(person.displayName)} label={person.displayName || "Unknown"} style={{
        fontSize: T.fsXxs, fontWeight: T.fwBold, color: T.white, background: color,
        opacity: person.active === false ? 0.45 : 1, border: demo ? `1.5px dashed ${T.card}` : "none",
      }} />
      {person.isPrimary && <span aria-label="primary" style={{ position: "absolute", right: -2, bottom: -2, width: 7, height: 7, borderRadius: "50%", background: T.pendingAmber, border: `2px solid ${T.card}` }} />}
      </span>
      {names && <span style={{ fontSize: T.fsXs, color: T.dark, maxWidth: 60, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", opacity: person.active === false ? 0.45 : 1 }}>{person.displayName?.trim().split(/\s+/)[0] || "Unknown"}</span>}
    </span>
  );
}

export function PeopleRolesMatrix({ authIdentity = "unbound", ...props }) {
  return <ParticipantMatrix key={authIdentity} authIdentity={authIdentity} {...props} />;
}

function ParticipantMatrix({ projects, currentRole, authIdentity, onOpenRecord, density = "comfortable", controlsTarget, countTarget, total = projects?.length || 0 }) {
  const isAdmin = currentRole === "admin";
  const [rowsState, setRowsState] = useState({ loading: true, error: null, byId: {}, errors: {} });
  const [directory, setDirectory] = useState({ loaded: false, error: null, people: [] });
  const [editing, setEditing] = useState(null);
  const [notice, setNotice] = useState(null);
  const [missing, setMissing] = useState("off");
  const [sortDir, setSortDir] = useState(0);
  const display = useParticipantDisplayPreference(authIdentity);

  const rows = useMemo(() => (projects || []).map((p) => ({ id: p.id, name: p.name, region: p.locationRegion || p.region || null, sub: p.locationName || p.city || "" })), [projects]);
  const idsKey = rows.map((r) => r.id).join(",");

  const loadMe = useCallback(async () => {
    try {
      const r = await getMyAssignments();
      return r;
    } catch { return null; }
  }, []);

  const loadRows = useCallback(async (onlyIds) => {
    const ids = onlyIds || (idsKey ? idsKey.split(",") : []);
    if (!onlyIds) setRowsState((s) => ({ ...s, loading: true, error: null }));
    const pairs = await Promise.all(ids.map((id) => Promise.resolve().then(() => getProjectParticipants(id))
      .then((r) => {
        if (!isParticipantArray(r?.participants)) throw new Error("Team unavailable: invalid response.");
        return [id, r.participants, null];
      }).catch((err) => [id, null, err?.message || "Could not load participants."])));
    setRowsState((s) => {
      const byId = { ...s.byId }, errors = { ...s.errors };
      for (const [id, list, err] of pairs) { if (list) { byId[id] = list; delete errors[id]; } else { delete byId[id]; errors[id] = err; } }
      const currentIds = idsKey ? idsKey.split(",") : [];
      const allFailed = currentIds.length > 0 && currentIds.every(id => errors[id] && !byId[id]);
      return { loading: false, error: allFailed ? errors[currentIds[0]] : null, byId, errors };
    });
  }, [idsKey]);

  useEffect(() => { loadMe(); }, [loadMe]);
  useEffect(() => { loadRows(); }, [loadRows]);

  const manageable = () => canManageRow({ role: currentRole });
  const anyManageable = rows.some((r) => manageable(r.id));
  useEffect(() => { if (!isAdmin) setEditing(null); }, [isAdmin]);

  const loadDirectory = useCallback(async () => {
    try {
      const r = await getPeopleDirectory();
      setDirectory({ loaded: true, error: null, people: r?.people || [] });
    } catch (err) { setDirectory({ loaded: true, error: err?.message || "Directory unavailable.", people: [] }); }
  }, []);
  useEffect(() => { if (anyManageable && !directory.loaded) loadDirectory(); }, [anyManageable, directory.loaded, loadDirectory]);

  // Any write outcome (success or refusal) refetches that row and identity.
  const revalidate = useCallback(async (projectId, lostAccess) => {
    await Promise.all([loadMe(), loadRows([projectId])]);
    if (lostAccess) {
      setNotice("Your access to that row changed. The matrix now shows your current permissions.");
      setDirectory({ loaded: false, error: null, people: [] });
    }
  }, [loadMe, loadRows]);

   const visibleRows = coverageRows(rows, rowsState.byId, { missing, sortDir });
   const stats = coverage(visibleRows, rowsState.byId);
  const unavailable = rows.filter(row => !Array.isArray(rowsState.byId[row.id]));
   // Reserve Names + overflow/no-primary space in both modes, including compact:
   // an optimistic preference save must not move any existing row.
   const rowHeight = 52;

  // Focus target from the Project drawer's admin Assign (?participantsProject=).
  // Read-only navigation aid: scrolls/highlights the row after a successful
  // load; editing still goes through the existing admin-gated cell controls.
  const routerSearch = useContext(UNSAFE_LocationContext)?.location?.search;
  const focusId = new URLSearchParams(routerSearch ?? (typeof window === "undefined" ? "" : window.location.search)).get("participantsProject");
  const focused = useRef(null);
  const rowLoaded = Boolean(focusId && rowsState.byId[focusId]);
  useEffect(() => {
    if (!focusId || !rowLoaded || focused.current === focusId) return;
    if (missing !== "off") { setMissing("off"); return; }
    const el = [...document.querySelectorAll("[data-testid^='people-roles-row-']")].find((n) => n.getAttribute("data-testid") === `people-roles-row-${focusId}`);
    if (!el) return;
    focused.current = focusId;
    el.scrollIntoView?.({ block: "center" });
    el.focus?.({ preventScroll: true });
  }, [focusId, rowLoaded, missing]);

  const count = <span data-testid="people-roles-summary">Displaying {visibleRows.length} of {total} · {stats.assignments} assigned · <span style={{ color: T.ragAmberText }}>{stats.gaps} gaps</span></span>;
  const controls = <>
    <label style={{ ...muted, display: "inline-flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" }}>Missing
      <select aria-label="Missing" value={missing} onChange={e => setMissing(e.target.value)} style={{ ...inputStyle, width: 132, height: 32, padding: "3px 6px" }}>
        {MISSING_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
      </select>
    </label>
    <ToolbarViewToggle ariaLabel="Team display" value={display.value} onChange={display.save} disabled={display.loading || display.saving}
      segments={[{ key: "initials", label: "Initials" }, { key: "names", label: "Names" }]} />
    {!isAdmin && <span data-testid="people-roles-readonly" title="Read-only · admins edit" style={{ ...muted, display: "inline-flex", gap: 4, alignItems: "center", whiteSpace: "nowrap" }}><Lock size={12} aria-hidden="true" />Read-only · admins edit</span>}
    <ParticipantsKey />
  </>;

  return (
    <section data-testid="people-roles" aria-label="Team">
      <style>{`
        [data-testid="people-roles"] .participant-gap { opacity: .6; }
        [data-testid="people-roles"] tr:hover .participant-gap,
        [data-testid="people-roles"] button:focus-visible .participant-gap { opacity: 1; }
        [data-testid="people-roles"] .participant-row:hover { background: ${T.hoverBg}; }
      `}</style>

      {controlsTarget ? createPortal(controls, controlsTarget) : <div style={{ display: "flex", alignItems: "center", gap: 8, minHeight: 40 }}>{controls}</div>}
      {countTarget ? createPortal(count, countTarget) : <div style={muted}>{count}</div>}
      {(display.loading || display.saving || display.error) && createPortal(
      <div data-testid="participant-preference-status" aria-label="Team display preference" style={{ position: "fixed", bottom: 24, right: 24, zIndex: 50, maxWidth: "min(400px, calc(100vw - 48px))", padding: "10px 14px", borderRadius: 8, border: `1px solid ${T.border}`, background: T.card, fontSize: T.fsXs }}>
      {display.loading && <div role="status" style={muted}>Loading display preference…</div>}
      {display.saving && <div role="status" style={muted}>Saving display preference…</div>}
      {display.error && <div role="alert" style={{ ...muted, color: T.red }}>Display preference not persisted: {display.error} <button type="button" style={btn} onClick={display.retry}>Retry display preference</button></div>}
      </div>, document.body)}
      {unavailable.length > 0 && !rowsState.loading && <div role="status" style={{ ...muted, marginBottom: 8 }}>Coverage incomplete: {unavailable.length} project reads unavailable. Assigned and gaps include displayed, loaded projects only.</div>}
      {missing !== "off" && unavailable.map(row => <div role="alert" key={row.id} style={{ ...muted, marginBottom: 8 }}>{row.name}: {rowsState.errors[row.id] || "Team unavailable."} Coverage unknown. <button type="button" style={btn} onClick={() => loadRows([row.id])}>Retry {row.name}</button></div>)}
      {missing !== "off" && !rowsState.loading && visibleRows.length === 0 && <div role="status">No loaded projects match Missing. Select Off (show all) to see all projects.</div>}

      {notice && <div role="status" data-testid="people-roles-notice" style={{ ...muted, fontSize: T.fsSm, marginBottom: 8 }}>{notice}</div>}
      {anyManageable && directory.error && <div role="alert" data-testid="people-roles-directory-error" style={{ color: T.red, fontSize: T.fsSm, marginBottom: 8 }}>{directory.error}</div>}

      {rowsState.loading && !Object.keys(rowsState.byId).length ? (
        <div data-testid="people-roles-loading" aria-busy="true" style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {[0, 1, 2, 3, 4].map((i) => <div key={i} style={{ height: 34, borderRadius: 6, background: T.lightGray }} />)}
        </div>
      ) : rowsState.error ? (
        <div role="alert" data-testid="people-roles-error" style={{ display: "flex", gap: 10, alignItems: "center", padding: "10px 14px", borderRadius: 8, background: T.utilRed, border: `1px solid ${T.redRing}` }}>
          <span style={{ flex: 1, fontSize: T.fsSm }}>{rowsState.error}</span>
          <button type="button" style={btn} onClick={() => loadRows()}>Retry</button>
        </div>
      ) : rows.length === 0 ? (
        <div data-testid="people-roles-empty" style={{ border: `1px dashed ${T.border}`, borderRadius: 10, padding: "22px 20px", background: T.lightGray }}>
          <div style={{ fontWeight: T.fwBold, color: T.dark }}>No active projects in view</div>
          <p style={{ ...muted, fontSize: T.fsSm, margin: "4px 0 0" }}>Adjust the Projects filters above to bring projects into the matrix.</p>
        </div>
      ) : (
        <ProjectsTableScroll overflowAffordance={false} testId="projects-participants-scroll" style={{ border: `1px solid ${T.border}`, borderRadius: 12, ...TABLE_TOP_CORNERS, background: T.card }}>
          <table data-testid="people-roles-table" data-row-height={rowHeight} style={{ borderCollapse: "collapse", tableLayout: "fixed", width: "100%", minWidth: 1100, fontSize: T.fsSm }}>
            <colgroup><col style={{ width: 230 }} /><col style={{ width: 64 }} />{MATRIX_COLUMNS.map(c => <col key={c.id} />)}</colgroup>
            <thead>
              <tr>
                 <th colSpan={2} style={TH} aria-label="Project identity" />
                 <th colSpan={3} scope="colgroup" style={{ ...TH, textAlign: "center", borderBottom: `2px solid ${T.border}`, borderRight: `1px solid ${T.border}` }}>Ownership</th>
                 <th colSpan={4} scope="colgroup" style={{ ...TH, textAlign: "center", borderBottom: `2px solid ${T.border}` }}>Tracks</th>
              </tr>
              <tr>
                <th style={{ ...TH, textAlign: "left", position: "sticky", left: 0, zIndex: 2 }}>Project</th>
                 <th style={{ ...TH, textAlign: "center", padding: "9px 2px" }} aria-sort={sortDir ? sortDir > 0 ? "ascending" : "descending" : "none"}><button type="button" style={{ font: "inherit", color: "inherit", textTransform: "inherit", border: 0, background: "transparent", padding: 0, cursor: "pointer" }} onClick={() => setSortDir(v => v === 1 ? -1 : 1)}>Filled {sortDir === 1 ? "↑" : sortDir === -1 ? "↓" : "↕"}</button></th>
                 {MATRIX_COLUMNS.map((c) => <th key={c.id} style={{ ...TH, textAlign: "center", whiteSpace: "normal", borderRight: c.id === "delivery_owner" ? `1px solid ${T.border}` : undefined }}>{c.label.replace(/ \(.+\)$/, "")}{c.key === null && <small style={{ display: "block", color: T.light, fontSize: T.fsXxs, marginTop: 2 }}>{c.id === "delivery_owner" ? "Ripple" : "JLL"}</small>}</th>)}
              </tr>
            </thead>
            <tbody>
               {visibleRows.map((row) => {
                    const list = rowsState.byId[row.id];
                    const canEdit = Boolean(list) && manageable(row.id);
                    return (
                       <tr key={row.id} className="participant-row" data-testid={`people-roles-row-${row.id}`} tabIndex={row.id === focusId ? -1 : undefined} aria-current={row.id === focusId ? "true" : undefined}
                         style={{ height: rowHeight, borderBottom: `1px solid ${T.border}`, ...(row.id === focusId ? { outline: `2px solid ${T.blue}`, outlineOffset: -2 } : null) }}>
                         <td style={{ padding: "0 12px", position: "sticky", left: 0, background: T.card }}>
                           <button type="button" title={row.name} onClick={e => onOpenRecord?.(row.id, e.currentTarget)} style={{ font: "inherit", fontWeight: T.fwBold, color: T.dark, border: 0, padding: 0, background: "transparent", cursor: "pointer", maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{row.name}</button>
                           {row.sub && density !== "compact" && <div style={{ ...muted, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={row.sub}>{row.sub}</div>}
                           {rowsState.errors[row.id] && <div role="alert" style={{ color: T.red, fontSize: T.fsXxs }}>{rowsState.errors[row.id]} <button type="button" style={btn} onClick={() => loadRows([row.id])}>Retry</button></div>}
                        </td>
                         <td data-testid={`people-roles-filled-${row.id}`} style={{ textAlign: "center", fontWeight: T.fwBold, color: row.filled === null ? T.mid : row.filled < 4 ? T.ragAmberText : T.dark }}>{row.filled === null ? <span title="Participant coverage unavailable">Unknown</span> : `${row.filled}/7`}</td>
                        {MATRIX_COLUMNS.map((c) => {
                          const people = list ? cellPeople(list, c) : [];
                          const content = !list ? <span style={muted}>-</span> : people.length === 0 ? (
                              <span className={canEdit ? "participant-gap" : undefined} data-testid={`people-roles-gap-${row.id}-${c.id}`} title="Unassigned (coverage gap)" style={canEdit ? { display: "inline-flex", width: 24, height: 24, boxSizing: "border-box", borderRadius: "50%", border: `1.5px dashed ${T.ragAmberText}`, color: T.ragAmberText, alignItems: "center", justifyContent: "center", fontSize: T.fsXs } : { color: T.light }}>{canEdit ? "+" : "–"}</span>
                          ) : (
                            <span style={{ display: "inline-flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
                               <span style={{ display: "inline-flex", gap: 2, alignItems: "center" }}>{people.slice(0, display.value === "names" ? 1 : 3).map((p) => <Avatar key={p.id} person={p} names={display.value === "names"} size={rowHeight === 28 ? 22 : 26} />)}{people.length > (display.value === "names" ? 1 : 3) && <span tabIndex={0} aria-label={people.slice(display.value === "names" ? 1 : 3).map(p => `${p.displayName} · ${p.org || "affiliation not recorded"}${p.isPrimary ? " · primary" : ""}`).join(", ")} title={people.slice(display.value === "names" ? 1 : 3).map(p => `${p.displayName} · ${p.org || "affiliation not recorded"}${p.isPrimary ? " · primary" : ""}`).join(", ")} style={{ ...muted, fontSize: T.fsXxs }}>+{people.length - (display.value === "names" ? 1 : 3)}</span>}</span>
                              {!cellHasPrimary(people) && <span data-testid={`people-roles-noprimary-${row.id}-${c.id}`} style={{ fontSize: T.fsXxs, color: T.ragAmberText }}>no primary</span>}
                            </span>
                          );
                          return (
                             <td key={c.id} data-testid={`people-roles-cell-${row.id}-${c.id}`} style={{ textAlign: "center", padding: 0, borderRight: c.id === "delivery_owner" ? `1px solid ${T.border}` : undefined }}>
                              {canEdit ? (
                                <button type="button" data-testid={`people-roles-edit-${row.id}-${c.id}`} aria-label={`Edit ${c.label} on ${row.name}`} onClick={() => setEditing({ projectId: row.id, projectName: row.name, col: c })}
                                   style={{ border: "none", background: "transparent", cursor: "pointer", padding: rowHeight === 28 ? 0 : 2, borderRadius: 6 }}>{content}</button>
                              ) : content}
                            </td>
                          );
                        })}
                      </tr>
                    );
              })}
            </tbody>
          </table>
        </ProjectsTableScroll>
      )}


      {isAdmin && <PortfolioRolesPanel directory={directory} onNeedDirectory={() => { if (!directory.loaded) loadDirectory(); }} />}

      {isAdmin && editing && (
        <CellEditor
          key={`${editing.projectId}-${editing.col.id}`} editing={editing}
          participants={rowsState.byId[editing.projectId] || []} directory={directory}
          onClose={() => setEditing(null)}
          onWritten={(lost) => revalidate(editing.projectId, lost).then(() => { if (lost) setEditing(null); })}
        />
      )}
    </section>
  );
}

function CellEditor({ editing, participants, directory, onClose, onWritten }) {
  const { projectId, projectName, col } = editing;
  const people = cellPeople(participants, col);
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const inFlight = useRef(false);
  const removeButtons = useRef(new Map());
  const cancelButton = useRef(null);
  const restoreRemove = useRef(null);
  const assigned = new Set(people.map((p) => p.personId));
  const options = pickable(directory.people, query).filter((p) => !assigned.has(personKey(p))).slice(0, 30);

  const cancelRemoval = useCallback(() => {
    if (inFlight.current) return;
    restoreRemove.current = confirm?.id;
    setConfirm(null);
  }, [confirm]);

  useEffect(() => {
    if (confirm) cancelButton.current?.focus();
    else if (restoreRemove.current) {
      removeButtons.current.get(restoreRemove.current)?.focus();
      restoreRemove.current = null;
    }
  }, [confirm]);

  // Own confirmation Escape before the shared modal's document bubble listener.
  useEffect(() => {
    if (!confirm) return undefined;
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      e.preventDefault();
      e.stopImmediatePropagation();
      cancelRemoval();
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [confirm, cancelRemoval]);

  const run = async (id, fn) => {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(id); setError(null);
    try { await fn(); setBusy(null); setConfirm(null); await onWritten(false); }
    catch (err) { setBusy(null); setError(err?.message || "The change could not be saved."); await onWritten(err?.name === "AuthError"); }
    finally { inFlight.current = false; }
  };

  return (
    <Modal isOpen onClose={() => !busy && onClose()} maxWidth={480} title={`${col.label} · ${projectName}`} ariaLabelledBy="people-roles-editor-title" testId="people-roles-editor">
      <div style={{ padding: "12px 18px 18px" }}>
        <div style={{ ...muted, marginBottom: 8 }}>Assign people to this track. Star sets the primary.</div>
        {people.length === 0 ? <div data-testid="people-roles-editor-empty" style={{ ...muted, color: T.ragAmberText, marginBottom: 8 }}>Nobody assigned. This is a coverage gap.</div> : (
          <ul style={{ listStyle: "none", padding: 0, margin: "0 0 10px", display: "flex", flexDirection: "column", gap: 4 }}>
            {people.map((p) => (
              <li key={p.id} data-testid={`people-roles-assigned-${p.id}`} style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 8px", borderRadius: 8, background: T.blueSoft }}>
                {confirm?.id === p.id ? (
                  <div data-testid="people-roles-remove-confirm" style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 6, width: "100%" }}>
                    <span style={{ flex: "1 1 220px", fontSize: T.fsSm, color: T.dark }}>Remove {p.displayName?.trim().split(/\s+/)[0] || "Unknown"} from {col.label}? Notes and history stay.</span>
                    <button ref={cancelButton} type="button" disabled={Boolean(busy)} style={withDisabled(btn, Boolean(busy))} onClick={cancelRemoval}>Cancel</button>
                    <button type="button" data-testid="people-roles-remove-confirm-btn" disabled={Boolean(busy)}
                      onClick={() => run(`rm-${p.id}`, () => deleteProjectParticipant(projectId, p.id))}
                      style={withDisabled({ ...btn, color: T.red, background: "transparent", border: "none" }, Boolean(busy))}>
                      {busy === `rm-${p.id}` ? "Removing…" : "Remove"}
                    </button>
                  </div>
                ) : <>
                <Avatar person={p} owner={col.key === null} />
                <span style={{ flex: 1, fontSize: T.fsSm, color: T.dark }}>{p.displayName}{p.isDemo ? " (demo)" : ""}{p.active === false ? " · inactive" : ""}</span>
                <button type="button" aria-pressed={Boolean(p.isPrimary)} disabled={Boolean(p.isPrimary) || Boolean(busy) || p.active === false} data-testid={`people-roles-star-${p.id}`}
                  title={p.isPrimary ? "Primary (DRI)" : "Set as primary"} onClick={() => run(`star-${p.id}`, () => putProjectParticipant(projectId, { personId: p.personId, trackKey: col.key, ownershipSlot: col.ownershipSlot, isPrimary: true }))}
                  style={withDisabled({ ...btn, color: p.isPrimary ? T.pendingAmber : T.mid }, Boolean(busy) && !p.isPrimary)}>{p.isPrimary ? "Primary" : "Make primary"}</button>
                <button ref={(node) => { if (node) removeButtons.current.set(p.id, node); else removeButtons.current.delete(p.id); }} type="button" disabled={Boolean(busy)} data-testid={`people-roles-remove-${p.id}`} onClick={() => { setConfirm(p); setError(null); }} style={withDisabled(btn, Boolean(busy))}>Remove</button>
                </>}
              </li>
            ))}
          </ul>
        )}

        {error && <div role="alert" data-testid="people-roles-editor-error" style={{ color: T.red, fontSize: T.fsSm, marginBottom: 8 }}>{error}</div>}

        <label style={{ fontSize: T.fsXs, fontWeight: T.fwBold, color: T.dark }} htmlFor="people-roles-search">Add a person</label>
        {directory.error ? <div role="alert" style={{ color: T.red, fontSize: T.fsSm }}>{directory.error}</div> : !directory.loaded ? <div style={muted}>Loading directory…</div> : (
          <>
            <input id="people-roles-search" data-testid="people-roles-search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Find a person by name, email or org…" style={{ ...inputStyle, margin: "4px 0 6px" }} />
            <ul style={{ listStyle: "none", padding: 0, margin: 0, maxHeight: 220, overflowY: "auto" }}>
              {options.length === 0 && <li style={muted}>No active people match.</li>}
              {options.map((p) => (
                <li key={personKey(p)}>
                  <button type="button" disabled={Boolean(busy)} data-testid={`people-roles-pick-${personKey(p)}`}
                    onClick={() => run(`add-${personKey(p)}`, () => putProjectParticipant(projectId, { personId: personKey(p), trackKey: col.key, ownershipSlot: col.ownershipSlot, isPrimary: people.length === 0 }))}
                    style={{ display: "flex", width: "100%", gap: 8, alignItems: "center", border: "none", background: "transparent", padding: "6px 8px", borderRadius: 7, cursor: "pointer", textAlign: "left", fontSize: T.fsSm, color: T.dark }}>
                    <Avatar person={{ ...p, isPrimary: false }} owner={col.key === null} />
                    <span style={{ flex: 1 }}>{p.displayName || p.email}<span style={{ ...muted, marginLeft: 6 }}>{p.email}</span></span>
                  </button>
                </li>
              ))}
            </ul>
            <div style={{ ...muted, marginTop: 4 }}>The first person added to an empty slot becomes its primary. Inactive people cannot be newly assigned.</div>
          </>
        )}
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 10 }}>
          <button type="button" style={btn} data-testid="people-roles-editor-done" onClick={onClose} disabled={Boolean(busy)}>Done</button>
        </div>
      </div>
    </Modal>
  );
}

function PortfolioRolesPanel({ directory, onNeedDirectory }) {
  const [open, setOpen] = useState(false);
  const [state, setState] = useState({ loading: false, error: null, byPerson: {} });
  const [rowError, setRowError] = useState({});
  const [busy, setBusy] = useState(null);
  const [query, setQuery] = useState("");

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try { const r = await getPortfolioRoles(); setState({ loading: false, error: null, byPerson: rolesByPerson(r?.roles) }); }
    catch (err) { setState((s) => ({ ...s, loading: false, error: err?.message || "Could not load portfolio roles." })); }
  }, []);

  useEffect(() => { if (open) { load(); onNeedDirectory(); } }, [open, load, onNeedDirectory]);

  const toggle = async (pid, role, on) => {
    const next = toggledRoles(state.byPerson[pid], role, on);
    setBusy(pid); setRowError((e) => ({ ...e, [pid]: null }));
    try {
      const r = await putPortfolioRoles(pid, next);
      setState((s) => ({ ...s, byPerson: { ...s.byPerson, [pid]: (r?.roles || []).map((x) => x.role) } }));
    } catch (err) { setRowError((e) => ({ ...e, [pid]: err?.message || "Could not save." })); load(); }
    setBusy(null);
  };

  const holders = Object.keys(state.byPerson).filter((k) => state.byPerson[k].length);
  const list = pickable(directory.people, query).filter((p) => query || holders.includes(personKey(p)));

  return (
    <details data-testid="portfolio-roles" onToggle={(e) => setOpen(e.currentTarget.open)} style={{ marginTop: 14, border: `1px solid ${T.border}`, borderRadius: 10, background: T.card, padding: "8px 12px" }}>
      <summary style={{ cursor: "pointer", fontSize: T.fsSm, fontWeight: T.fwBold, color: T.dark }}>Portfolio roles · admin</summary>
      <p style={{ ...muted, margin: "6px 0" }}>Delivery Director and Delivery Owner per person. Changing one keeps the other; application access is not affected.</p>
      {state.error && <div role="alert" style={{ color: T.red, fontSize: T.fsSm }}>{state.error}</div>}
      {directory.error && <div role="alert" style={{ color: T.red, fontSize: T.fsSm }}>{directory.error}</div>}
      <input data-testid="portfolio-roles-search" aria-label="Find a person for portfolio roles" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Find a person to add a role…" style={{ ...inputStyle, maxWidth: 320, margin: "4px 0 8px" }} />
      {state.loading ? <div style={muted}>Loading…</div> : list.length === 0 ? <div data-testid="portfolio-roles-empty" style={muted}>{query ? "No active people match." : "Nobody holds a delivery role yet. Search to assign one."}</div> : (
        <table style={{ borderCollapse: "collapse", fontSize: T.fsSm }}>
          <tbody>
            {list.map((p) => {
              const pid = personKey(p);
              return (
                <tr key={pid} data-testid={`portfolio-roles-row-${pid}`}>
                  <td style={{ padding: "4px 10px 4px 0", color: T.dark }}>{p.displayName || p.email} <OrgChip org={p.org} /></td>
                  {Object.entries(PORTFOLIO_ROLE_LABELS).map(([role, label]) => (
                    <td key={role} style={{ padding: "4px 8px" }}>
                      <label style={{ display: "inline-flex", gap: 4, alignItems: "center", fontSize: T.fsXs }}>
                        <input type="checkbox" data-testid={`portfolio-roles-${pid}-${role}`} disabled={busy === pid} checked={(state.byPerson[pid] || []).includes(role)} onChange={(e) => toggle(pid, role, e.target.checked)} />{label}
                      </label>
                    </td>
                  ))}
                  <td>{rowError[pid] && <span role="alert" style={{ color: T.red, fontSize: T.fsXs }}>{rowError[pid]}</span>}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </details>
  );
}
